import { useEffect, useState } from "react";
import {
	Box,
	Button,
	Flex,
	Icon,
	Input,
	Spinner,
	Stack,
	Text
} from "@chakra-ui/react";
import {
	RiArrowRightLine,
	RiSearchLine,
	RiAddLine,
	RiUser3Line,
	RiProfileLine,
	RiPhoneLine,
	RiMap2Line
} from "react-icons/ri";
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useHistory } from "react-router-dom";

import { useAuth } from "../../hooks/auth";
import { useCart } from "../../hooks/cart";
import api from "../../services/api";
import Header from "../../components/Header";
import ModalConsumidor from "../../components/ModalConsumidor";
import DataTableClient from "../../components/DataTableClient";


type ClientFormData = {
	doc: string;
}
interface ClienteData {
	id: string;
	nome: string;
	doc: string;
	phone: string;
	cid: string;
}

const clientFormSchema: yup.SchemaOf<ClientFormData> = yup.object().shape({
	doc: yup.string().required('Documento obrigatório'),
});

const SelectClient: React.FC = () => {
	const { company, user } = useAuth();
	const { addCartCliente, addCartMerchant } = useCart();
	const history = useHistory();

	const [loading, setLoading] = useState(false);
	const [cliente, setCliente] = useState<ClienteData>({} as ClienteData);
	const [clientes, setClientes] = useState<ClienteData[]>([]);
	const [isVisibleTable, setIsVisibleTable] = useState(false);

	const [isOpenCart, setIsOpenCart] = useState(false);
	const [isOpenModalConsumidor, setIsOpneModalConsumidor] = useState(false);

	const { register, handleSubmit, formState, reset } = useForm<ClientFormData>({
		resolver: yupResolver(clientFormSchema)
	});

	useEffect(() => {
		addCartMerchant(String(company.id), user.comissao);
	}, []);

	const handleClienteDoc: SubmitHandler<ClientFormData> = async (values: ClientFormData) => {
		await api.get(`/controle/client/doc/${values.doc}`)
			.then(response => {
				setCliente(response.data);
				reset();
			})
	}

	async function handleClientes() {
		setLoading(true);
		await api.get('controle/client/index')
			.then(response => {
				setIsVisibleTable(true);
				setClientes(response.data);
				setLoading(false);
			})
			.catch(() => setLoading(false))
	}

	return (
		<Flex
			w="100vw"
			h="100vh"
			direction="column"
		>
			<Header isOpenCart={setIsOpenCart} />

			{isOpenModalConsumidor && (
				<ModalConsumidor
					isOpen={isOpenModalConsumidor}
					onRequestClose={setIsOpneModalConsumidor}
				/>
			)}

			<Flex
				flexDir="column"
				justify="space-between"
				align="center"
				mt="45px"
				p="4"
			>
				<Flex w="100%" flexDirection="column" align="center">
					<Flex flexDirection="column" align="center">
						<Text fontSize={["18", "23"]} color="gray.700" align="center">Adicionar Cliente</Text>
						<Text fontSize={["14", "18"]} color="gray.500" align="center">
							Adicione um cliente pesquisando pelo documento ou pequisando no geral.
						</Text>
					</Flex>

					<Flex
						w={["100%", "100%", "100%", "50%", "50%"]}
						px="6"
						py="2"
						justify="center"
						align="center"
					>
						<Stack direction="row" spacing="2" align="center">
							<Button
								colorScheme="blue"
                bg="blue.500"
								borderRadius="full"
								px="2"
								onClick={() => history.push('/client/cad')}
							>
								<Icon as={RiAddLine} fontSize="20" />
								<Text pl={["0", "2"]} fontSize={["14", "16"]}>Novo cliente</Text>
							</Button>

							<Button
								colorScheme="blackAlpha"
								borderRadius="full"
								fontSize={["14", "16"]}
								onClick={() => setIsOpneModalConsumidor(true)}
							>
								Consumidor
							</Button>
						</Stack>
					</Flex>
				</Flex>
			</Flex>

			{!isVisibleTable ? (
				<>
					{/* BUTTON BUSCAR CLIENTE DOC */}
					<Flex
						as="form"
						justify="center"
						onSubmit={handleSubmit(handleClienteDoc)}
					>
						<Flex
							as="label"
							flex="1"
							py="1"
							pl="4"
							pr="2"
							mx="2"
							maxWidth={400}
							alignSelf="center"
							color="gray.900"
							position="relative"
							bg="gray.50"
							borderRadius="full"
						>
							<Input
								color="gray.900"
								variant="unstyled"
								px="4"
								mr="4"
								placeholder="Buscar cliente por documento"
								_placeholder={{ color: 'gray.400' }}
								{...register("doc")}
							/>

							<Button
								isLoading={formState.isSubmitting}
								type="submit"
								p="0"
								bg="blue.500"
								borderRadius="full"
								_hover={{ bg: "blue.200" }}
							>
								<Icon as={RiSearchLine} fontSize="20" color="white" />
							</Button>
						</Flex>
					</Flex>

					{/* BUTTON PESQUISA GERAL DE CLIENTE */}
					<Flex justify="center" align="center" m="8">
						{!cliente.id ? (
							<Button
								bg="gray.100"
								color="gray.400"
								px="6"
								py="4"
								border="2px"
								borderColor="gray.300"
								borderRadius="full"
								onClick={handleClientes}
							>
								Pesquisa geral de clientes
								{loading && <Spinner size="sm" color="gray.500" ml="4" />}
							</Button>
						) : (
							<Flex flexDir="column">
								<Box as="button" textAlign="end" py="2"
									onClick={() => setCliente({} as ClienteData)}
								>
									<Text color="blue.500" fontWeight={500} _hover={{ color: "blue.200" }}>Limpar</Text>
								</Box>

								<Stack
									bgGradient='linear(to-r, gray.50, gray.100)'
									borderRadius={8}
									boxShadow='xl'
									p="4"
									w="300px"
								>
									<Flex align="center">
										<Icon as={RiUser3Line} fontSize="18" mr="2" />
										<Text color="gray.800" textTransform="uppercase" fontWeight="500">{cliente.nome}</Text>
									</Flex>

									<Flex align="center">
										<Icon as={RiProfileLine} fontSize="18" mr="2" />
										<Text color="gray.600">{cliente.doc}</Text>
									</Flex>

									<Flex align="center">
										<Icon as={RiPhoneLine} fontSize="18" mr="2" />
										<Text color="gray.500">{cliente.phone}</Text>
									</Flex>

									<Stack direction="row" justify="space-between" align="center">
										<Flex align="center">
											<Icon as={RiMap2Line} fontSize="18" mr="2" />
											<Text color="gray.800">{cliente.cid}</Text>
										</Flex>

										<Button
											bg="transparent"
											p={0}
											m={0}
											onClick={() => {
												addCartCliente(cliente, false);
												// history.push('/productsaux');
												history.push('/products');
											}}
										>
											<Icon as={RiArrowRightLine} color="blue.500" fontSize="24" />
										</Button>
									</Stack>
								</Stack>
							</Flex>
						)}
					</Flex>
				</>
			) : (
				<>
					{clientes.length > 0 && (
						<Flex mx="10">
							<DataTableClient data={clientes} />
						</Flex>
					)}
				</>
			)}
		</Flex>
	)
}

export default SelectClient;
